/**
 * Phase 8 · C-10 — Live time-series for the Dashboard weekly chart.
 *
 * Aggregates from existing base tables (no pre-materialised rollup table):
 *   - applications / shortlisted → ggj_b2b_applications
 *   - outreach                   → ggj_b2b_outbox_events (email.* event types)
 *   - interviews                 → ggj_b2b_interview_events
 *
 * Since PostgREST doesn't expose date_trunc() against typed enums without an RPC,
 * we fetch the `created_at` column filtered by org + range and bucket in JS. This
 * keeps the service call dependency-free and avoids adding a migration, per the
 * ticket's "DON'T create a new aggregation table" constraint.
 */
import { supabase } from '../client';

export type AnalyticsMetric = 'applications' | 'shortlisted' | 'outreach' | 'interviews';
export type AnalyticsRange = '7d' | '30d' | '90d';
export type AnalyticsGranularity = 'daily' | 'weekly' | 'monthly';

export interface TimeSeriesPoint {
  /** ISO date (UTC midnight) for the bucket start. */
  bucket: string;
  value: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function rangeDays(range: AnalyticsRange): number {
  return range === '7d' ? 7 : range === '30d' ? 30 : 90;
}

/** UTC midnight of the given Date. */
function utcMidnight(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** ISO week start (Monday, UTC midnight). */
function utcWeekStart(d: Date): Date {
  const m = utcMidnight(d);
  const dow = m.getUTCDay(); // 0 = Sun … 6 = Sat
  const offsetToMonday = (dow + 6) % 7; // 0 if Mon, 6 if Sun
  return new Date(m.getTime() - offsetToMonday * DAY_MS);
}

/** UTC midnight of the first day of the month. */
function utcMonthStart(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/**
 * Generate the ordered list of bucket keys (UTC midnight ISO strings) that should
 * appear on the chart, even when the DB returns zero rows for some buckets.
 */
function buildEmptyBuckets(range: AnalyticsRange, granularity: AnalyticsGranularity): string[] {
  const now = new Date();
  const days = rangeDays(range);
  const start = utcMidnight(new Date(now.getTime() - (days - 1) * DAY_MS));

  const out: string[] = [];
  if (granularity === 'daily') {
    for (let i = 0; i < days; i++) {
      const d = new Date(start.getTime() + i * DAY_MS);
      out.push(d.toISOString());
    }
    return out;
  }

  if (granularity === 'weekly') {
    // weekly: buckets are week starts (Mondays), stepping forwards from earliest
    const currentWeekStart = utcWeekStart(now);
    const earliestWeekStart = utcWeekStart(start);
    const weeks = Math.max(
      1,
      Math.round((currentWeekStart.getTime() - earliestWeekStart.getTime()) / (7 * DAY_MS)) + 1,
    );
    for (let i = 0; i < weeks; i++) {
      const d = new Date(earliestWeekStart.getTime() + i * 7 * DAY_MS);
      out.push(d.toISOString());
    }
    return out;
  }

  // monthly: buckets are the 1st of each month
  const earliestMonth = utcMonthStart(start);
  const currentMonth = utcMonthStart(now);
  let cur = earliestMonth;
  while (cur.getTime() <= currentMonth.getTime()) {
    out.push(cur.toISOString());
    cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
  }
  if (out.length === 0) out.push(currentMonth.toISOString());
  return out;
}

function bucketKeyFor(isoTimestamp: string, granularity: AnalyticsGranularity): string | null {
  const t = Date.parse(isoTimestamp);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  if (granularity === 'daily') return utcMidnight(d).toISOString();
  if (granularity === 'weekly') return utcWeekStart(d).toISOString();
  return utcMonthStart(d).toISOString();
}

interface QueryShape {
  table: string;
  /** Optional narrowing predicate applied to the rows returned from the table. */
  filter?: (row: Record<string, unknown>) => boolean;
  /** Additional PostgREST filter clauses applied at the query level. */
  applyExtraFilters?: (
    q: ReturnType<ReturnType<typeof supabase.from>['select']>,
  ) => ReturnType<ReturnType<typeof supabase.from>['select']>;
}

function queryShapeFor(metric: AnalyticsMetric): QueryShape {
  switch (metric) {
    case 'applications':
      return { table: 'ggj_b2b_applications' };
    case 'shortlisted':
      return {
        table: 'ggj_b2b_applications',
        applyExtraFilters: (q) => q.eq('status', 'shortlisted'),
      };
    case 'outreach':
      return {
        table: 'ggj_b2b_outbox_events',
        applyExtraFilters: (q) => q.like('event_type', 'email.%'),
      };
    case 'interviews':
      return { table: 'ggj_b2b_interview_events' };
  }
}

export const analyticsWeeklyService = {
  /**
   * Fetch a time-series for the given metric, bucketed by granularity, inside the range.
   * Always returns a fully-filled array (zero-valued buckets for empty periods).
   *
   * Throws on network/RLS errors — callers should catch and fall back to a zero series.
   */
  async getTimeSeries(
    orgId: string,
    metric: AnalyticsMetric,
    range: AnalyticsRange,
    granularity: AnalyticsGranularity,
  ): Promise<TimeSeriesPoint[]> {
    const buckets = buildEmptyBuckets(range, granularity);
    if (!orgId || buckets.length === 0) return [];

    const firstBucketMs = Date.parse(buckets[0]);
    const cutoffIso = new Date(firstBucketMs).toISOString();

    const shape = queryShapeFor(metric);
    const base = supabase
      .from(shape.table)
      .select('created_at')
      .eq('org_id', orgId)
      .gte('created_at', cutoffIso)
      .order('created_at', { ascending: true })
      .limit(5000);

    const q = shape.applyExtraFilters ? shape.applyExtraFilters(base) : base;
    const { data, error } = await q;
    if (error) throw new Error(error.message);

    const counts = new Map<string, number>();
    for (const b of buckets) counts.set(b, 0);

    const rows = (data ?? []) as Array<Record<string, unknown>>;
    for (const row of rows) {
      if (shape.filter && !shape.filter(row)) continue;
      const createdAt = row['created_at'];
      if (typeof createdAt !== 'string') continue;
      const key = bucketKeyFor(createdAt, granularity);
      if (!key) continue;
      if (!counts.has(key)) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return buckets.map((b) => ({ bucket: b, value: counts.get(b) ?? 0 }));
  },

  /** Convenience: a zero-value series matching the shape the chart expects. */
  zeroSeries(range: AnalyticsRange, granularity: AnalyticsGranularity): TimeSeriesPoint[] {
    return buildEmptyBuckets(range, granularity).map((b) => ({ bucket: b, value: 0 }));
  },
};
